// store/index.js — root Vuex store for tracko-web.
// Purpose: the store root. The deployed pro-ui keeps almost no state at the root
// — it is a registry of dynamically-registered namespaced modules (Pattern C).
// This root holds app-lifecycle flags + the seams the auth engine expects at the
// root (the `clearStore` action dispatched by user/logOut, and the
// `service_error` mutation + '/server-error' redirect for status.code 12), and it
// registers the auth/session core modules (`user`, `mfa-store`) so the backbone
// boots standalone. Other feature modules (`paasUser`, `states`, `trackers`,
// `report-view`, `entity-form`, …) are registered by src/engines.js at boot and
// by each screen on demand.
// Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
// (module names `user`/`mfa-store`, root action `clearStore`, root mutation
// `service_error` — AUTH-SESSION §2.3/§2.6), internal impl re-authored.

import Vue from 'vue'
import Vuex from 'vuex'
import { registerModuleSafe as _registerModuleSafe, unregisterModuleSafe as _unregisterModuleSafe } from './register-module-safe'
import userModule from './modules/user.js'
import mfaModule from './modules/mfa.js'
import { setStore } from '../api/context.js'

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: false,
  state: {
    // set true once user/get_info has hydrated the session (auth engine calls setReady)
    ready: false,
    // toggled by the global error table on status.code === 12
    serviceError: false
  },
  getters: {
    ready: (state) => state.ready,
    serviceError: (state) => state.serviceError
  },
  mutations: {
    // VERBATIM name — auth engine's `setReady` after user/get_info hydrate.
    setReady(state, value = true) {
      state.ready = value
    },
    // VERBATIM name — commit("service_error") on backend status.code 12.
    service_error(state) {
      state.serviceError = true
    }
  },
  actions: {
    // VERBATIM name — dispatched by user/logOut before pushing /login. Resets the
    // shell root and the core auth modules; namespaced feature modules clear their
    // own state via their own resets. Registering engines can extend this.
    clearStore({ commit }) {
      commit('setReady', false)
      if (typeof store.hasModule === 'function') {
        if (store.hasModule('user')) commit('user/resetState')
        if (store.hasModule('mfa-store')) commit('mfa-store/resetState')
      }
      return Promise.resolve()
    }
  }
})

// Expose the Pattern-C helper on the store instance so screens can call
// `this.$store.registerModuleSafe(...)` exactly as the deployed code does.
store.registerModuleSafe = (path, mod, preserveState) =>
  _registerModuleSafe(store, path, mod, preserveState)
store.unregisterModuleSafe = (path) => _unregisterModuleSafe(store, path)

// Named helpers bound to this singleton store (the `registerModuleSafe(name,
// module, preserveState)` lifecycle helper screens import directly).
export function registerModuleSafe(path, mod, preserveState) {
  return _registerModuleSafe(store, path, mod, preserveState)
}
export function unregisterModuleSafe(path) {
  return _unregisterModuleSafe(store, path)
}
export function isModuleRegistered(path) {
  return typeof store.hasModule === 'function' ? store.hasModule(path) : false
}

// Register the auth/session core so the backbone works without waiting on
// src/engines.js. Idempotent — if engines.js also registers `user`/`mfa-store`
// via registerModuleSafe, those calls no-op. `mfa-store` is eagerly registered
// (LIVE registers it on the login screen) so `user/auth`'s root dispatches to
// `mfa-store/*` always resolve.
registerModuleSafe('user', userModule)
registerModuleSafe('mfa-store', mfaModule)

// Make the store reachable from the request layer (which dispatches
// `user/logOut` from the status-code error table) without a circular import.
setStore(store)

export default store
